import type { IEntry } from '@app/domain/instrument/instrument.interface'
import { QuestionTypeId } from '@app/domain/instrument/instrument.interface'
import type { IPatient } from '@app/domain/patient/patient.interface'
import type {
  IDataInstrument,
  IScreen,
  IVisit,
  IWorkflowStep,
} from '@app/domain/visit/visit.interface'
import { VisitUseCase } from '@app/domain/visit/visit.interface'

import { VisitStage } from './state'

import type { IVisitState } from './state'

export interface IApiEntry {
  id: string
  visitId: string
  instrumentId: string
  instrumentVersion: number
  responses?: Record<string, string>
  derivedValues?: Record<string, unknown>
}

export function convertApiEntry(entry: IApiEntry): IEntry {
  return {
    id: entry.id,
    visitId: entry.visitId,
    instrumentId: entry.instrumentId,
    instrumentVersion: entry.instrumentVersion,
    responses: entry.responses,
    updatedAt: null,
    derivedValues: entry.derivedValues,
  }
}

export const buildVisitContextWithResponses = (
  visit: IVisit,
  allInstruments: IDataInstrument[],
  entries: Record<string, IEntry>,
  patient?: IPatient,
) => {
  const allInstrumentResponses = {}
  allInstruments.forEach(function (instrument) {
    const responsesLookupTable = {}
    const entry = entries[instrument.id]
    if (instrument.questions) {
      Object.keys(instrument.questions).map((qid) => {
        const question = instrument.questions[qid]
        if (question.inputType === QuestionTypeId.Checkbox) {
          responsesLookupTable[question.id] = entry?.responses[
            question.id
          ]?.split('|')
        } else {
          responsesLookupTable[question.id] = entry?.responses[question.id]
        }
      })
    }

    allInstrumentResponses[instrument.id] = responsesLookupTable
  })

  const visitAndResponses = {
    visit: visit,
    instrumentResponses: allInstrumentResponses,
    patient: patient ?? {},
  }

  return visitAndResponses
}

export const evaluateConfiguredLogic = <T extends Record<string, unknown>>(
  configuredLogic: string,
  context: T,
) => {
  try {
    const contextKeys = Object.keys(context)
    const contextValues = Object.values(context)

    const shouldDisplay = new Function(
      ...contextKeys,
      `return (${configuredLogic});`,
    ) as (...args: unknown[]) => boolean

    return shouldDisplay(...contextValues)
  } catch (error) {
    console.error('failed to evaluate configured logic.', error)
    return false
  }
}

export const isScreenLogicValid = (screen: IScreen) => {
  return (
    screen !== null &&
    typeof screen.flowLogic !== 'undefined' &&
    screen.flowLogic !== ''
  )
}

export const determineNextStage = (
  currentState: IVisitState,
  isSignatureRequired: boolean,
  isBypassPatientSignatureFlagEnabled: boolean,
): IVisitState => {
  switch (currentState.stage) {
    case VisitStage.Collection: {
      if (isBypassPatientSignatureFlagEnabled || !isSignatureRequired) {
        return {
          ...currentState,
          stage: VisitStage.Completed,
        }
      } else {
        return {
          ...currentState,
          stage: VisitStage.Signature,
        }
      }
    }
    case VisitStage.Signature:
      return {
        ...currentState,
        stage: VisitStage.Completed,
      }
    default:
      return {
        ...currentState,
      }
  }
}

export const visitHasResponses = (entries: IEntry[]) => {
  let entryHasResponses = false
  if (Object.keys(entries).length > 0) {
    Object.keys(entries).forEach((entry) => {
      if (Object.keys(entries[entry].responses).length > 0) {
        entryHasResponses = true
      }
    })
  }
  return entryHasResponses
}

export function countCompletedScreens(
  instruments: IDataInstrument[],
  instrumentSteps: IWorkflowStep[],
  workflowIdx: number,
  savedVisitStateScreen?: number,
) {
  let completedScreens = 0

  if (workflowIdx === 1) {
    const firstStep = instrumentSteps[0]

    if (firstStep.type === 'instrument') {
      completedScreens += calculateScreens(firstStep.instrumentId)
    } else if (firstStep.type === 'demographic-confirmation') {
      completedScreens += 1
    }
  } else {
    for (let i = 0; i < workflowIdx; i++) {
      const step = instrumentSteps[i]

      if (step.type === 'instrument') {
        completedScreens += calculateScreens(step.instrumentId)
      } else if (step.type === 'demographic-confirmation') {
        completedScreens += 1
      }
    }
  }

  completedScreens += savedVisitStateScreen ?? 0
  return completedScreens

  function calculateScreens(instrumentId: string): number {
    const instrument = instruments.find((inst) => inst.id === instrumentId)

    if (instrument && instrument.views) {
      return instrument.views
        .filter((v) => v.useCase === VisitUseCase.Intake)
        .reduce((total, view) => {
          view.screens.sort((s1, s2) => s1.position - s2.position)
          return total + view.screens.length
        }, 0)
    }
    return 0
  }
}

export function reduceProgress(state: IVisitState): IVisitState {
  const currentProgress = state.progress ?? 0
  return {
    ...state,
    progress:
      state.workflowStep === 0
        ? currentProgress
        : Math.max(0, currentProgress - 1),
  }
}
